/**
 * The auth provider, and the Firebase wiring it is the only consumer of.
 *
 * Split out of auth.tsx so that file exports the context and `useAuth` alone:
 * react-refresh can only keep component state across an edit when a module
 * exports components and nothing else, and a file holding both the provider and
 * the hook loses fast refresh for every screen that reads auth — which is all of
 * them.
 *
 * The Firebase `initializeApp` call moved with it deliberately. It runs at module
 * load, so it now happens only where the provider is actually mounted; importing
 * `useAuth` no longer drags the SDK (and env.ts's fail-fast config check) into
 * the module graph.
 */
import { initializeApp } from "firebase/app";
import {
  connectAuthEmulator,
  createUserWithEmailAndPassword,
  getAuth,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  type User,
} from "firebase/auth";
import { useEffect, useMemo, useState, type ReactNode } from "react";

import { AuthContext, type AuthState } from "./auth.js";
import { AUTH_EMULATOR_HOST, FIREBASE_CONFIG } from "./env.js";

const app = initializeApp(FIREBASE_CONFIG);
const auth = getAuth(app);

if (AUTH_EMULATOR_HOST) {
  connectAuthEmulator(auth, `http://${AUTH_EMULATOR_HOST}`, { disableWarnings: false });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Three states, not two. `undefined` means "we do not know yet" — Firebase
  // restores a session asynchronously on load, and collapsing that into `null`
  // would flash the sign-in page at every already-signed-in user on every reload.
  const [user, setUser] = useState<User | null | undefined>(undefined);

  useEffect(() => onAuthStateChanged(auth, setUser), []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      async signIn(email, password) {
        await signInWithEmailAndPassword(auth, email, password);
      },
      async createIdentity(email, password) {
        // Signs the new user in as a side effect, which is Firebase's behaviour
        // and not something we can switch off. It is also what we want: the very
        // next call needs their ID token to provision the account.
        await createUserWithEmailAndPassword(auth, email, password);
      },
      async signOut() {
        await fbSignOut(auth);
      },
      async sendPasswordReset(email) {
        try {
          await sendPasswordResetEmail(auth, email);
        } catch (err) {
          /*
           * SUCCEED QUIETLY WHEN THE ADDRESS IS UNKNOWN.
           *
           * Firebase's newer email-enumeration protection already collapses this
           * case, but it is a per-project console setting that can be off — and a
           * security property that depends on a setting nobody in this repo can
           * see is not a property. So the collapse happens here too, where it is
           * visible and testable.
           *
           * What it buys: this form must not answer "does this address have an
           * account". The people using this platform are mid-purchase on land,
           * and confirming one has a case here is worth something to a party on
           * the other side of that transaction.
           *
           * A transport failure is NOT swallowed. Someone offline needs to learn
           * the request never left, or they will sit waiting for mail that was
           * never asked for.
           */
          const code = (err as { code?: string } | null)?.code ?? "";
          const isUnknownAddress =
            code === "auth/user-not-found" ||
            code === "auth/invalid-email" ||
            code === "auth/invalid-recipient-email";
          if (!isUnknownAddress) throw err;
        }
      },
      async getToken() {
        // The SDK caches and refreshes for us; ID tokens live an hour. Not forcing
        // a refresh means most calls cost nothing, and the ones near expiry
        // refresh automatically.
        return auth.currentUser ? auth.currentUser.getIdToken() : null;
      },
    }),
    [user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
